'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { ChevronDown } from 'lucide-react';
import Link from 'next/link';

const faqs = [
  {
    q: 'What is Griott?',
    a: 'Griott is a community-curated digital archive of Zimbabwean and African history. Contributors submit historical accounts, oral traditions, and sourced records. Human curators review every submission before it is published.',
  },
  {
    q: 'Who can contribute to the archive?',
    a: 'Anyone can register and submit a contribution. You do not need to be an academic or professional historian. We welcome oral traditions, family histories, community records, and sourced academic content equally — as long as each is correctly labelled with its certainty level.',
  },
  {
    q: 'How does the moderation process work?',
    a: 'Every submission is reviewed by a trained human curator. Curators may approve, request revisions, or reject submissions. We also use AI-assisted citation analysis to help contributors verify claims and add credible sources before submission.',
  },
  {
    q: 'What are the certainty labels?',
    a: 'We use three labels: Oral Tradition (passed down verbally, not independently verified), Sourced Claim (supported by cited external sources), and Firsthand Account (written by someone who directly witnessed or experienced the event). These help readers assess the nature of each entry.',
  },
  {
    q: 'Is Griott free to use?',
    a: 'Yes. Reading the archive is completely free and always will be. Creating an account and contributing is also free. We may introduce optional supporter tiers in the future to fund curation and platform development.',
  },
  {
    q: 'Can I submit content anonymously?',
    a: 'You can request that your name not be displayed publicly on a submission. However, you must have a registered account so curators can contact you if they need clarification.',
  },
  {
    q: 'What is a Documentation Project?',
    a: 'A Documentation Project is a structured effort to document a specific subject — a family, clan, tribe, cultural practice, location, or historical event. Contributors can start a project, invite collaborators, and link individual stories to the project\'s timeline.',
  },
  {
    q: 'How do I report inaccurate content?',
    a: 'Every published entry has a "Report" button. You can flag inaccuracies, disputed claims, or inappropriate content. Our curator team reviews all reports.',
  },
  {
    q: 'Is my personal data safe?',
    a: 'We take data privacy seriously. We do not sell your data. See our Privacy Policy for full details on what we collect and how we use it.',
  },
  {
    q: 'How can I partner with Griott?',
    a: 'We welcome partnerships with academic institutions, cultural organisations, NGOs, and aligned businesses. Visit our Partner With Us page or contact us directly.',
  },
];

export default function FAQPage() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
              Help
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-base" style={{ color: 'var(--muted-foreground)' }}>
              Everything you need to know about Griott. Can't find your answer?{' '}
              <Link href="/contact" className="hover:underline" style={{ color: 'var(--primary)' }}>
                Contact us
              </Link>.
            </p>
          </div>

          <div className="space-y-2">
            {faqs?.map((faq, i) => (
              <div
                key={i}
                className="rounded-xl border overflow-hidden"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left gap-4"
                >
                  <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{faq?.q}</span>
                  <ChevronDown
                    size={16}
                    className="flex-shrink-0 transition-transform duration-200"
                    style={{
                      color: 'var(--muted-foreground)',
                      transform: open === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>
                {open === i && (
                  <div className="px-6 pb-5">
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{faq?.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link href="/contact" className="text-sm hover:underline" style={{ color: 'var(--primary)' }}>
              Contact us →
            </Link>
            <Link href="/contributor-guide" className="text-sm hover:underline" style={{ color: 'var(--muted-foreground)' }}>
              Contributor Guide →
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
